import { useCallback, useEffect, useRef, useState } from 'react'
import { putExternalCredentials } from '@/services/accounting'
import {
  CloverExtensionError,
  getCloverKeepaliveStatus,
  isCloverExtensionAvailable,
  requestCloverTokensFromExtension,
  setCloverKeepalive,
  type CloverKeepaliveStatus,
} from '@/services/cloverExtension'
import { isBackgroundJobActive, type BackgroundJobStatus } from '@/services/jobs'
import { toast } from '@/store/toastStore'
import {
  buildCloverPutPayload,
  cloverTokensChanged,
  shouldKeepaliveOnAfterJob,
  type CloverPutCredentials,
} from './cloverTokenSync'

const TOKEN_SYNC_INTERVAL_MS = 5 * 60 * 1000
const KEEPALIVE_STATUS_POLL_MS = 30_000

export function useCloverJobSession(jobStatus: BackgroundJobStatus | null = null) {
  const [extensionInstalled, setExtensionInstalled] = useState<boolean | null>(null)
  const [keepaliveStatus, setKeepaliveStatus] = useState<CloverKeepaliveStatus | null>(null)
  const [keepalivePinned, setKeepalivePinned] = useState(true)
  const [keepaliveBusy, setKeepaliveBusy] = useState(false)

  const lastPushedRef = useRef<CloverPutCredentials | null>(null)
  const prevJobActiveRef = useRef(false)
  const syncInFlightRef = useRef(false)
  const keepaliveInitializedRef = useRef(false)

  const jobActive = jobStatus != null && isBackgroundJobActive(jobStatus)
  const waitingForTokens = jobStatus === 'waiting_for_clover_tokens'

  const refreshKeepaliveStatus = useCallback(async () => {
    if (!extensionInstalled) return
    try {
      const status = await getCloverKeepaliveStatus()
      setKeepaliveStatus(status)
    } catch {
      // Status polling is best-effort; UI stays usable.
    }
  }, [extensionInstalled])

  useEffect(() => {
    let cancelled = false
    void isCloverExtensionAvailable().then((installed) => {
      if (!cancelled) setExtensionInstalled(installed)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!extensionInstalled) return
    void refreshKeepaliveStatus()
    const interval = window.setInterval(() => {
      void refreshKeepaliveStatus()
    }, KEEPALIVE_STATUS_POLL_MS)
    return () => window.clearInterval(interval)
  }, [extensionInstalled, refreshKeepaliveStatus])

  useEffect(() => {
    if (!extensionInstalled || keepaliveInitializedRef.current) return
    keepaliveInitializedRef.current = true
    void (async () => {
      try {
        const status = await getCloverKeepaliveStatus()
        setKeepaliveStatus(status)
        if (!status.enabled) {
          await setCloverKeepalive(true)
          setKeepalivePinned(true)
          await refreshKeepaliveStatus()
        }
      } catch {
        // Best-effort default; user can toggle manually.
      }
    })()
  }, [extensionInstalled, refreshKeepaliveStatus])

  const syncTokensIfChanged = useCallback(async (showToasts: boolean) => {
    if (syncInFlightRef.current) return
    syncInFlightRef.current = true
    try {
      const tokens = await requestCloverTokensFromExtension()
      const payload = buildCloverPutPayload(tokens)
      if (!cloverTokensChanged(lastPushedRef.current, payload)) {
        return
      }
      await putExternalCredentials('clover', payload)
      lastPushedRef.current = payload
      if (showToasts) {
        toast.success('Clover tokens updated.')
      }
    } catch (err) {
      if (showToasts) {
        if (err instanceof CloverExtensionError && err.code === 'LOGIN_REQUIRED') {
          toast.warning('Log in to Clover in the other tab, then try again.')
        } else if (err instanceof CloverExtensionError && err.code === 'NOT_INSTALLED') {
          setExtensionInstalled(false)
        } else {
          const message = err instanceof Error ? err.message : 'Failed to sync Clover tokens.'
          toast.error(message)
        }
      }
    } finally {
      syncInFlightRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!extensionInstalled || !jobActive) return

    const interval = window.setInterval(() => {
      void syncTokensIfChanged(false)
    }, TOKEN_SYNC_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [extensionInstalled, jobActive, syncTokensIfChanged])

  useEffect(() => {
    if (!extensionInstalled) {
      prevJobActiveRef.current = jobActive
      return
    }

    if (jobActive && !prevJobActiveRef.current) {
      void setCloverKeepalive(true).then(() => refreshKeepaliveStatus()).catch(() => {})
    }

    if (!jobActive && prevJobActiveRef.current) {
      const keepOn = shouldKeepaliveOnAfterJob(keepalivePinned, false)
      if (!keepOn) {
        void setCloverKeepalive(false).then(() => refreshKeepaliveStatus()).catch(() => {})
      }
    }

    prevJobActiveRef.current = jobActive
  }, [extensionInstalled, jobActive, keepalivePinned, refreshKeepaliveStatus])

  useEffect(() => {
    if (!extensionInstalled || !waitingForTokens) return
    void syncTokensIfChanged(false)
  }, [extensionInstalled, waitingForTokens, syncTokensIfChanged])

  const handleKeepaliveToggle = useCallback(
    async (enabled: boolean) => {
      setKeepaliveBusy(true)
      try {
        await setCloverKeepalive(enabled)
        setKeepalivePinned(enabled)
        await refreshKeepaliveStatus()
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update keepalive setting.'
        toast.error(message)
      } finally {
        setKeepaliveBusy(false)
      }
    },
    [refreshKeepaliveStatus],
  )

  const markTokensPushed = useCallback((payload: CloverPutCredentials) => {
    lastPushedRef.current = payload
  }, [])

  return {
    extensionInstalled,
    keepaliveStatus,
    keepaliveBusy,
    handleKeepaliveToggle,
    markTokensPushed,
  }
}
